<template>
  <div class="AutomaticCarousel position-relative" :class="{ 'no-controls': !hasOverflow }">
    <slick
      ref="slick"
      :options="finalSlickOptions"
      @beforeChange="handleBeforeSwipe"
      @afterChange="onAfterChange"
      class="justify-content-start h-100"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        class="carousel-item h-100 pb-4 pt-2 px-4"
      >
        <slot :item="item" :index="index" />
      </div>
    </slick>
    <button
      v-if="hasOverflow"
      class="carousel-pause-btn"
      @click="toggleAutoplay"
      :title="isAutoplayActive ? 'Pause autoplay' : 'Play autoplay'"
    >
      <i :class="isAutoplayActive ? 'fas fa-pause' : 'fas fa-play'"></i>
    </button>
  </div>
</template>

<script>
import slick from "vue-slick";
import carouselArrowsMixin from "@/mixins/carouselArrowsMixin";

export default {
  name: "AutomaticCarousel",
  components: {
    slick
  },
  mixins: [carouselArrowsMixin],
  props: {
    items: {
      type: Array,
      default: () => []
    },
    slickOptions: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
      defaultSlickOptions: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: false,
        autoplay: true,
        autoplaySpeed: 5000,
        // Don't pause on hover/focus: on mobile, scrolling the page requires
        // touching the carousel, which would focus it and stop autoplay from
        // starting. Pausing happens only on explicit controls (pause button +
        // dot/arrow clicks); an incidental scroll-touch does not.
        pauseOnHover: false,
        pauseOnFocus: false,
        arrows: true,
        dots: true,
        adaptiveHeight: false,
        responsive: [
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1
            }
          },
          {
            breakpoint: 992,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 1
            }
          },
          {
            breakpoint: 1315,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 1
            }
          }
        ]
      },
      isAutoplayActive: true,
      rewindTimer: null
    };
  },
  computed: {
    hasOverflow() {
      const count = this.items.length;
      if (this.windowWidth < 768) return count > 1;
      if (this.windowWidth < 992) return count > 2;
      return count > 3;
    },
    adjustedSlickOptions() {
      const options = { ...this.defaultSlickOptions };
      if (this.items.length < 3) {
        options.slidesToShow = this.items.length;
        options.responsive = this.defaultSlickOptions.responsive.map(bp => ({
          ...bp,
          settings: {
            ...bp.settings,
            slidesToShow: Math.min(bp.settings.slidesToShow, this.items.length)
          }
        }));
      }
      return options;
    },
    finalSlickOptions() {
      return {
        ...this.adjustedSlickOptions,
        ...this.slickOptions,
        ...this.getArrowConfig()
      };
    }
  },
  methods: {
    toggleAutoplay() {
      this.isAutoplayActive = !this.isAutoplayActive;
      if (this.$refs.slick) {
        if (this.isAutoplayActive) {
          this.$refs.slick.play();
        } else {
          this.$refs.slick.pause();
        }
      }
    },
    // Clicking a dot or arrow is manual navigation — stop the autoplay.
    pauseOnManualNav(e) {
      if (!this.isAutoplayActive || !e.target || !e.target.closest) return;
      if (
        e.target.closest(".slick-dots") ||
        e.target.closest(".bottom-arrow") ||
        e.target.closest(".slick-arrow")
      ) {
        this.isAutoplayActive = false;
        if (this.$refs.slick) this.$refs.slick.pause();
        clearTimeout(this.rewindTimer);
      }
    },
    onAfterChange(event, slick, currentSlide) {
      this.handleSwipe(currentSlide);

      // When autoplay reaches the last visible position, wait one interval then
      // jump back to slide 0 so the carousel loops without cloning slides.
      // Cloned slides lose Vue event listeners and become unclickable (CR-97).
      const slidesToShow = slick.slickGetOption("slidesToShow");
      const lastIndex = slick.slideCount - slidesToShow;

      clearTimeout(this.rewindTimer);
      if (currentSlide >= lastIndex && this.isAutoplayActive) {
        const interval = slick.slickGetOption("autoplaySpeed") || 5000;
        this.rewindTimer = setTimeout(() => {
          if (!this.$refs.slick || !this.isAutoplayActive) return;
          this.$refs.slick.goTo(0);
          this.$nextTick(() => {
            if (this.$refs.slick && this.isAutoplayActive) {
              this.$refs.slick.play();
            }
          });
        }, interval);
      }
    },
    // slick pauses its own autoplay on any drag/swipe and doesn't resume it, so
    // re-arm autoplay once the interaction ends (unless the user paused it).
    resumeAutoplayAfterInteraction() {
      clearTimeout(this._resumeTimer);
      this._resumeTimer = setTimeout(() => {
        if (this.isAutoplayActive && this.$refs.slick) this.$refs.slick.play();
      }, 350);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.centerActiveDot();
    });
    this._onResize = () => { this.windowWidth = window.innerWidth; };
    window.addEventListener("resize", this._onResize);
    this.$el.addEventListener("click", this.pauseOnManualNav);
    this.$el.addEventListener("mouseup", this.resumeAutoplayAfterInteraction);
    this.$el.addEventListener("touchend", this.resumeAutoplayAfterInteraction);
  },
  beforeDestroy() {
    clearTimeout(this.rewindTimer);
    clearTimeout(this._resumeTimer);
    window.removeEventListener("resize", this._onResize);
    if (this.$el && this.$el.removeEventListener) {
      this.$el.removeEventListener("click", this.pauseOnManualNav);
      this.$el.removeEventListener("mouseup", this.resumeAutoplayAfterInteraction);
      this.$el.removeEventListener("touchend", this.resumeAutoplayAfterInteraction);
    }
  }
};
</script>

<style scoped>
.AutomaticCarousel {
  width: 100%;
}

.AutomaticCarousel >>> .slick-track {
  display: flex !important;
  align-items: stretch;
}

.AutomaticCarousel >>> .slick-slide {
  height: auto !important;
  display: flex !important;
  flex-direction: column;
}

.AutomaticCarousel >>> .slick-slide > div {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.carousel-item {
  width: 100%;
  display: flex !important;
  flex-direction: column;
  height: 100%;
}

.carousel-pause-btn {
  position: absolute;
  bottom: 30px;
  left: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid white;
  background-color: rgba(0, 0, 143, 0.95);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.carousel-pause-btn:hover {
  background-color: rgba(0, 0, 143, 1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
}

.carousel-pause-btn i {
  font-size: 20px;
  color: white;
}

@media (max-width: 768px) {
  .carousel-pause-btn {
    width: 44px !important;
    height: 44px !important;
  }

  .carousel-pause-btn i {
    font-size: 18px !important;
  }
}
</style>
